import { randomUUID } from 'node:crypto'
import { Context, symbols, type Plugin } from '@tnega/core'
import { SessionLog, isAgentType, type AgentType, type SessionEvent } from '@tnega/session'
import type { ModelMessage } from '@tnega/session'
import { AgentInbox, AgentService } from './service.js'
import { AgentError } from './service.js'
import { findInterruptedTurn, recoverInterruptedTurn } from './recovery.js'
import { provideAgentSteer } from './steer.js'
import {
  DurableInbox,
  type DurableInboxMessage,
  type DurableTarget,
} from './inbox-durable.js'
import type {
  AgentCancelCause,
  AgentContextBudget,
  AgentHooks,
  AgentInput,
  AgentLoop,
  AgentRunResult,
  AgentStreamEvent,
  LLMAdapter,
} from './types.js'

export interface AgentSessionMeta {
  agentId: string
  subagentMode?: 'spawn' | 'fork'
  subagentAudience?: 'agent' | 'user'
  subagentLabel?: string
  subagentDepth?: number
  subagentAllowShell?: boolean
  subagentAllowNetwork?: boolean
  subagentPermission?: 'read-only' | 'workspace-write' | 'bypass'
  agentType?: AgentType
  mode?: 'auto' | 'plan' | 'goal' | 'execute'
  title?: string
  owner?: string
  parentSessionId?: string
  forkedAtMessageId?: string
  createdAt?: number
}

/** Synchronous finalizer invoked immediately before agent publication. */
export interface AgentSetupCommit {
  commit(): void
}

/** Compose an unpublished agent scope; a throw rejects the whole creation. */
export type AgentSetup = (
  agentCtx: Context,
) => AgentSetupCommit | Promise<AgentSetupCommit | void> | void

export interface LiveInboxView {
  readonly size: number
  snapshot(): { nextTurn: readonly DurableInboxMessage[]; nextStep: readonly DurableInboxMessage[] }
}

export type AgentStatus = 'idle' | 'running'

export interface AgentCancelOptions {
  keepInbox?: boolean
}

/** Public API intent for a newly inserted inbox message. */
export type AgentInboxInsertionIntent = 'followup' | 'steer' | 'inject'

/** Target carried by an inbox insertion observation. */
export type AgentInboxInsertedTarget = AgentInboxInsertionIntent | DurableTarget

export interface AgentInboxInsertedEvent {
  id: string
  agent: LiveAgent
  /**
   * New messages report their public API intent. Replacements report the
   * durable queue target that contained the replaced message.
   */
  target: AgentInboxInsertedTarget
  input: DurableInboxMessage
  inboxSeq: number
}

export interface LiveAgent {
  readonly id: string
  readonly meta: AgentSessionMeta
  readonly agentType: AgentType | undefined
  readonly mode: 'auto' | 'plan' | 'goal' | 'execute' | undefined
  readonly owner: string | undefined
  readonly parentSessionId: string | undefined
  readonly status: AgentStatus
  readonly inbox: LiveInboxView
  readonly session: SessionLog
  readonly ctx: Context
  /** The scoped context setup composes before publication. */
  readonly agentCtx: Context
  followup(input: AgentInput): Promise<void>
  steer(input: AgentInput): Promise<void>
  /** Durably enqueue a correction before aborting the current Run; preserve prior inbox items. */
  interrupt(input: AgentInput): Promise<void>
  replaceMessage(messageId: string, input: AgentInput): Promise<void>
  removeMessage(messageId: string): Promise<void>
  /** Durably stage model-visible input for the next step without waking idle work. */
  inject(input: AgentInput): Promise<void>
  send(input: AgentInput, options?: { mode?: 'followup' | 'steer' }): Promise<void>
  cancel(cause: AgentCancelCause, options?: AgentCancelOptions): void
  whenIdle(): Promise<void>
  /**
   * Drain currently queued durable work as a live event stream, one turn per
   * claimed inbox item. In manual-streaming mode this is the only drain; the
   * generator settles when the queue is empty, so callers stream a run and
   * stop at quiescence.
   */
  runTurns(signal?: AbortSignal): AsyncGenerator<AgentStreamEvent, void, void>
  runMaintenance<T>(task: (signal: AbortSignal) => Promise<T>): Promise<T>
  dispose(): Promise<void>
}

export interface AgentHandle {
  agent: LiveAgent
  dispose(): Promise<void>
}

export interface AgentCreationOptions {
  file: string
  /** Stable identity shared by this process and future resumes. */
  sessionId?: string
  agentType?: AgentType
  mode?: 'auto' | 'plan' | 'goal' | 'execute'
  title?: string
  owner?: string
  parentSessionId?: string
  subagentMode?: 'spawn' | 'fork'
  subagentAudience?: 'agent' | 'user'
  subagentLabel?: string
  subagentDepth?: number
  subagentAllowShell?: boolean
  subagentAllowNetwork?: boolean
  subagentPermission?: 'read-only' | 'workspace-write' | 'bypass'
  createdAt?: number
  forkedAtMessageId?: string
  id?: string
  /** Optional composition callback run before the agent is published. */
  setup?: AgentSetup
  llm?: LLMAdapter
  contextWindow?: number
  system?: string
  maxTurns?: number
  maxSteps?: number
  contextBudget?: AgentContextBudget
  hooks?: AgentHooks
  initial?: readonly AgentInput[]
  /** Disable the automatic drain; callers drive turns via `runTurns`. */
  manualStreaming?: boolean
  /**
   * On resume, continue a turn a crash cut off when that is safe (see
   * `findInterruptedTurn`): queue one recovery input for the next turn.
   */
  autoRecover?: boolean
}

export interface AgentFactory {
  create(options: AgentCreationOptions): Promise<AgentHandle>
  resume(options: AgentCreationOptions): Promise<AgentHandle>
}

function createAgentRuntimeContext(agentCtx: Context): Context {
  const runtimeCtx = agentCtx.extend()
  Object.defineProperty(runtimeCtx, Context.filter, {
    value: (target: Context) => {
      const targetScope = target[symbols.isolate]!.agentScope
      let ancestor = agentCtx
      while (true) {
        if (targetScope === ancestor[symbols.isolate]!.agentScope) return true
        if (ancestor.fiber.parent.fiber === ancestor.fiber) return false
        ancestor = ancestor.fiber.parent
      }
    },
  })
  const events = agentCtx.events as unknown as Record<
    string,
    (...args: unknown[]) => unknown
  >
  for (const method of ['emit', 'parallel', 'serial', 'bail', 'waterfall', 'waterfallAsync']) {
    Object.defineProperty(runtimeCtx, method, {
      value: (...args: unknown[]) => events[method]!(runtimeCtx, ...args),
    })
  }
  return runtimeCtx
}

class LiveAgentImpl implements LiveAgent {
  private _status: AgentStatus = 'idle'
  private _disposed = false
  private _controller: AbortController | undefined
  private _drainPromise: Promise<void> | undefined
  private _busy = false
  private _writeTail: Promise<void> = Promise.resolve()
  private _pendingWrite: Promise<void> | undefined
  private _sessionStarted = false
  private _maintenance:
    | { promise: Promise<void>; controller: AbortController }
    | undefined
  private _maintenanceBusy = false
  private _setupDisposers: Array<() => Promise<void> | void> = []
  private _scopeClosed = false
  private _durable: DurableInbox
  private _manualStreaming = false
  private _wakeReserved = false
  private _loop: AgentLoop | undefined

  constructor(
    private _ctx: Context,
    private _agentCtx: Context,
    private _service: AgentService,
    readonly id: string,
    readonly inbox: LiveInboxView,
    readonly session: SessionLog,
    durable: DurableInbox,
    readonly meta: AgentSessionMeta,
    manualStreaming = false,
    wakeReserved = false,
  ) {
    this._durable = durable
    this._manualStreaming = manualStreaming
    this._wakeReserved = wakeReserved
  }

  get agentType(): AgentType | undefined {
    return this.meta.agentType
  }

  get mode(): 'auto' | 'plan' | 'goal' | 'execute' | undefined {
    return this.meta.mode
  }

  get owner(): string | undefined {
    return this.meta.owner
  }

  get parentSessionId(): string | undefined {
    return this.meta.parentSessionId
  }

  get agentCtx(): Context {
    return this._agentCtx
  }

  trackSetupDispose(dispose: () => Promise<void> | void): void {
    if (this._scopeClosed) void dispose()
    else this._setupDisposers.push(dispose)
  }

  get status(): AgentStatus {
    return this._status
  }

  get ctx(): Context {
    return this._ctx
  }

  followup(input: AgentInput): Promise<void> {
    return this._send(input, 'followup')
  }

  setLoop(loop: AgentLoop | undefined): void {
    this._loop = loop
  }

  private async _closeCustomTurn(turn: number, run: AgentRunResult): Promise<void> {
    const events = await this.session.read()
    if (events.some(event => event.type === 'turn/end' && event.payload.turn === turn)) return
    const reason = run.finishReason === 'length'
      ? { kind: 'max-tokens' as const }
      : run.finishReason === 'max_steps'
        ? { kind: 'max-steps' as const }
        : run.finishReason === 'max_turns'
          ? { kind: 'max-turns' as const }
          : run.finishReason === 'cancelled'
            ? { kind: 'interrupted' as const }
            : run.finishReason === 'error'
              ? { kind: 'interrupted' as const }
              : { kind: 'completed' as const }
    await this.session.append('turn/end', {
      turn,
      finishReason: run.finishReason,
      reason,
      ...(run.output ? { output: run.output } : {}),
      ...(run.steps.length ? { steps: run.steps.length } : {}),
    })
  }

  private async _closeFailedCustomTurn(turn: number, error: unknown): Promise<void> {
    const events = await this.session.read()
    if (events.some(event => event.type === 'turn/end' && event.payload.turn === turn)) return
    const failure = error instanceof Error
      ? { name: error.name, message: error.message, ...(error.stack ? { stack: error.stack } : {}) }
      : { name: 'Error', message: String(error) }
    await this.session.append('turn/end', {
      turn,
      finishReason: 'error',
      reason: { kind: 'error', error: failure },
      interrupted: true,
      error: failure,
    })
  }

  /** Replace one pending inbox message by durable message id. */
  replaceMessage(messageId: string, input: AgentInput): Promise<void> {
    return this._mutatePending(async () => {
      const previous = this._durable.get(messageId)
      const target = this._findTarget(messageId)
      if (!previous || !target) return
      const replacement = await this._durable.replace(messageId, {
        text: input.text ?? '',
        ...(inboxContent(input) !== undefined ? { content: inboxContent(input) } : {}),
      })
      if (!replacement) return
      this._ctx.emit('agent/inbox/discarded', {
        id: this.id,
        agent: this,
        message: previous,
      })
      const event: AgentInboxInsertedEvent = {
        id: this.id,
        agent: this,
        target,
        input: replacement,
        inboxSeq: this._durable.size,
      }
      this._ctx.emit('agent/inbox/inserted', event)
    })
  }

  /** Remove one pending inbox message by durable message id. */
  removeMessage(messageId: string): Promise<void> {
    return this._mutatePending(async () => {
      const removed = await this._durable.remove(messageId)
      if (!removed) return
      this._ctx.emit('agent/inbox/discarded', {
        id: this.id,
        agent: this,
        message: removed,
      })
    })
  }

  steer(input: AgentInput): Promise<void> {
    return this._send(input, 'steer')
  }

  interrupt(input: AgentInput): Promise<void> {
    return this._send(input, 'followup', true)
  }

  inject(input: AgentInput): Promise<void> {
    return this._send(input, 'inject')
  }

  send(input: AgentInput, options: { mode?: 'followup' | 'steer' } = {}): Promise<void> {
    return this._send(input, options.mode === 'steer' ? 'steer' : 'followup')
  }

  private _send(
    input: AgentInput,
    target: AgentInboxInsertionIntent,
    interrupt = false,
  ): Promise<void> {
    const text = input.text ?? ''
    const content = inboxContent(input)
    const steeredAfterAbort = target === 'steer' && this._controller?.signal.aborted === true
    return this._mutatePending(async () => {
      // A resubmission (a client retry after a dropped connection or restart) is already in the log.
      if (input.requestId !== undefined && this._durable.admitted(input.requestId)) return
      const fields = {
        ...(input.requestId !== undefined ? { id: input.requestId } : {}),
        text,
        ...(content !== undefined ? { content } : {}),
      }
      const message = target === 'followup'
        ? await this._durable.insert(fields)
        : target === 'steer'
        ? steeredAfterAbort
          ? await this._durable.insert(fields)
          : await this._durable.steer(fields)
        : await this._durable.insert(fields, 'next-step')
      // The durable correction is ready before cancellation releases the old
      // Run. No queued older input can restart it without seeing this message.
      if (interrupt) this.cancel({ type: 'user' }, { keepInbox: true })
      if (target !== 'inject') this._wakeReserved = true
      const event: AgentInboxInsertedEvent = {
        id: this.id,
        agent: this,
        target,
        input: message,
        inboxSeq: this._durable.size,
      }
      this._ctx.emit('agent/inbox/inserted', event)
    }, target !== 'inject')
  }

  private _mutatePending(
    task: () => Promise<void>,
    wakes = false,
    allowDisposed = false,
  ): Promise<void> {
    if (this._disposed && !allowDisposed) {
      return Promise.reject(new Error(`agent disposed: ${this.id}`))
    }
    const result = this._writeTail.then(task)
    const recovered = result.catch((error: unknown) => {
      try {
        this._ctx.emit('agent/error', { id: this.id, error })
      } catch {
        // Observers must not poison the durable mutation queue.
      }
    })
    this._writeTail = recovered
    this._pendingWrite = recovered
    void result.then(
      () => {
        if (this._pendingWrite === recovered) this._pendingWrite = undefined
        if (wakes) queueMicrotask(() => this._wake())
      },
      () => {
        if (this._pendingWrite === recovered) this._pendingWrite = undefined
      },
    )
    return result
  }

  private _findTarget(messageId: string): 'next-turn' | 'next-step' | undefined {
    const snapshot = this._durable.snapshot()
    if (snapshot.nextStep.some(message => message.id === messageId)) return 'next-step'
    if (snapshot.nextTurn.some(message => message.id === messageId)) return 'next-turn'
    return undefined
  }

  cancel(cause: AgentCancelCause, options: AgentCancelOptions = {}): void {
    if (!options.keepInbox) {
      void this._mutatePending(async () => {
        const before = this._durable.snapshot()
        await this._durable.clear()
        for (const message of [...before.nextStep, ...before.nextTurn]) {
          this._ctx.emit('agent/inbox/discarded', { id: this.id, message })
        }
        this._wakeReserved = false
      }, false, true)
    }
    this._controller?.abort(cause)
    this._maintenance?.controller.abort(cause)
  }

  async whenIdle(): Promise<void> {
    while (true) {
      while (this._pendingWrite) await this._pendingWrite
      const maintenance = this._maintenance
      if (maintenance) {
        await maintenance.promise.catch(() => undefined)
        continue
      }
      const drain = this._drainPromise
      if (drain) {
        await drain.catch(() => undefined)
        continue
      }
      if (!this._hasQueuedWork()) break
      await new Promise<void>(resolve => setTimeout(resolve, 0))
    }
  }

  /** Run one non-turn maintenance task from the true idle phase. */
  runMaintenance<T>(task: (signal: AbortSignal) => Promise<T>): Promise<T> {
    if (this._disposed) throw new Error(`agent disposed: ${this.id}`)
    if (this._maintenanceBusy || this._drainPromise || this._pendingWrite) {
      throw new Error('agent is busy; maintenance can only run while idle')
    }
    this._maintenanceBusy = true
    const controller = new AbortController()
    const result = Promise.resolve()
      .then(async () => {
        const value = await task(controller.signal)
        return value
      })
      .finally(() => {
        this._maintenanceBusy = false
        if (this._maintenance?.controller === controller) {
          this._maintenance = undefined
        }
        queueMicrotask(() => this._wake())
      })
    this._maintenance = { promise: result.then(() => undefined, () => undefined), controller }
    return result
  }

  async dispose(): Promise<void> {
    if (this._disposed) return
    this._disposed = true
    this.cancel({ type: 'disposed' })
    await this._writeTail.catch(() => undefined)
    await this._drainPromise?.catch(() => undefined)
    if (this._maintenance) this._maintenance.controller.abort({ type: 'disposed' })
    await this._maintenance?.promise.catch(() => undefined)
    if (!this._scopeClosed) {
      this._scopeClosed = true
      await Promise.all([...this._setupDisposers].reverse().map(dispose => dispose()))
    }
    const writeTail = this._writeTail
    if (writeTail !== this._drainPromise && this._pendingWrite) {
      this._pendingWrite = undefined
      await writeTail.catch(() => undefined)
    }
    await this.session.close()
    this._setStatus('idle')
  }

  /** Publish the agent lifecycle session-start notification once. */
  async publishSessionStart(source: 'startup' | 'resume' = 'startup'): Promise<void> {
    if (this._sessionStarted) return
    this._sessionStarted = true
    this._ctx.emit('agent/session-start', {
      id: this.id,
      agent: this,
      source,
    })
  }

  /** Current durable turn number for a newly claimed inbox item. */
  private async _nextTurnNumber(): Promise<number> {
    const events = await this.session.read()
    let max = 0
    for (const event of events) {
      if (event.type === 'turn/start') max = Math.max(max, event.payload.turn)
    }
    return max + 1
  }

  /** Start draining currently pending durable work without a new input. */
  wakeNow(): void {
    this._wake()
  }

  private _wake(): void {
    if (
      this._disposed
      || this._manualStreaming
      || this._drainPromise
      || this._busy
      || this._maintenanceBusy
    ) return
    const task = this._drainAll()
    this._drainPromise = task
    void task.then(
      () => {
        if (this._drainPromise === task) this._drainPromise = undefined
        if (this._hasQueuedWork()) queueMicrotask(() => this._wake())
      },
      (error: unknown) => {
        if (this._drainPromise === task) this._drainPromise = undefined
        try {
          this._ctx.emit('agent/error', { id: this.id, agent: this, error })
        } catch {
          // Observers must not turn a contained drain failure into an unhandled rejection.
        }
      },
    )
  }

  private async _drainAll(): Promise<void> {
    await this._writeTail.catch(() => undefined)
    if (this._hasQueuedWork()) {
      // Automatic (library) drain: consume the queue, discarding the stream.
      for await (const _ of this._streamTurns()) void _
    }
  }

  /** Public drain over queued durable work (manual-streaming mode). */
  async *runTurns(signal?: AbortSignal): AsyncGenerator<AgentStreamEvent, void, void> {
    if (this._manualStreaming) await this._writeTail.catch(() => undefined)
    if (this._busy || this._disposed) return
    yield* this._streamTurns(signal)
  }

  private async *_streamTurns(
    signal?: AbortSignal,
  ): AsyncGenerator<AgentStreamEvent, void, void> {
    if (this._busy || this._disposed) return
    this._busy = true
    this._setStatus('running')
    try {
      while (!this._disposed) {
        // A waking steer may open a later turn if the preceding turn has
        // already passed its last next-step boundary. Inject stays inert.
        const snapshot = this._durable.snapshot()
        if (!snapshot.nextTurn.length && !this._wakeReserved) break
        const turn = await this._nextTurnNumber()
        await this.session.append('turn/start', { turn, reason: 'user' })
        const batch = await this._durable.claimBatch()
        this._wakeReserved = false
        const input = batch.length ? await this._inputForBatch(batch, true) : { text: '' }
        for (const message of batch) {
          this._ctx.emit('agent/inbox/claimed', {
            id: this.id,
            agent: this,
            message,
            input: { text: message.text ?? '' },
            turn,
          })
        }
        const controller = new AbortController()
        this._controller = controller
        const forward = (): void => controller.abort(signal?.reason)
        if (signal?.aborted) controller.abort(signal.reason)
        else signal?.addEventListener('abort', forward, { once: true })
        try {
          if (this._manualStreaming) {
            if (this._loop) {
              const run = await this._loop(input, { signal: controller.signal, turn })
              await this._closeCustomTurn(turn, run)
              yield { type: 'run/end', run }
            } else {
              yield* this._service.runStream(input, { signal: controller.signal, turn })
            }
          } else {
            if (this._loop) {
              const run = await this._loop(input, { signal: controller.signal, turn })
              await this._closeCustomTurn(turn, run)
            }
            else await this._service.run(input, { signal: controller.signal, turn })
          }
        } catch (error) {
          if (this._loop) await this._closeFailedCustomTurn(turn, error)
          if (!controller.signal.aborted) {
            const step = await this._failedStep()
            this._ctx.emit('agent/error', {
              id: this.id,
              agent: this,
              error,
              ...(step !== undefined ? { turn, step } : {}),
            })
            if (this._manualStreaming) throw error
          }
        } finally {
          signal?.removeEventListener('abort', forward)
          if (this._controller === controller) this._controller = undefined
        }
      }
    } finally {
      this._busy = false
      if (!this._durable.size) this._wakeReserved = false
      this._setStatus('idle')
    }
  }

  private _hasQueuedWork(): boolean {
    const snapshot = this._durable.snapshot()
    return snapshot.nextTurn.length > 0 || (this._wakeReserved && snapshot.nextStep.length > 0)
  }

  private async _inputForBatch(
    batch: readonly DurableInboxMessage[],
    includeHistory: boolean,
  ): Promise<AgentInput> {
    const history = includeHistory ? await this.session.deriveMessages() : []
    const messages = batch.flatMap(message => message.content !== undefined
      ? Array.isArray(message.content)
        ? message.content as ModelMessage[]
        : [{ role: 'user' as const, content: String(message.content ?? '') }]
      : message.text !== undefined
        ? [{ role: 'user' as const, content: message.text }]
        : [])
    // Structured inbox content carries message ownership (for example
    // agent:<id>). Collapsing a single message to text loses its name when
    // the Session records the next user/message event.
    if (history.length || messages.length > 1 || batch.some(message => message.content !== undefined)) {
      return { messages: [...history, ...messages] }
    }
    return { text: messages[0]?.content ?? '' }
  }

  async claimNextStepInputs(): Promise<readonly AgentInput[]> {
    await this._writeTail
    const batch = await this._durable.claimNextStep()
    // Claiming next-step input spends its wake reservation only after the
    // durable mutation succeeds. A failed append must remain wakeable.
    this._wakeReserved = false
    if (batch.length) {
      const turn = (await this._nextTurnNumber()) - 1
      for (const message of batch) {
        this._ctx.emit('agent/inbox/claimed', {
          id: this.id,
          agent: this,
          message,
          input: { text: message.text ?? '' },
          turn,
        })
      }
    }
    return batch.length ? [await this._inputForBatch(batch, false)] : []
  }

  private _setStatus(status: AgentStatus): void {
    const previous = this._status
    if (previous === status) return
    this._status = status
    this._ctx.emit('agent/status', { id: this.id, status, previous })
  }

  private async _failedStep(): Promise<number | undefined> {
    const events = await this.session.read()
    for (let index = events.length - 1; index >= 0; index -= 1) {
      const event = events[index]
      if (event?.type === 'step/end'
        && (event.payload.finishReason === 'error'
          || event.payload.interrupted === true)) {
        return event.payload.step
      }
    }
    return undefined
  }
}

export class AgentRegistry {
  private _agents = new Map<string, LiveAgent>()
  private _owners = new Map<string, string | undefined>()
  private _factory: AgentFactory | undefined

  constructor(private _ctx: Context) {}

  setFactory(factory: AgentFactory): () => void {
    this._factory = factory
    return () => {
      if (this._factory === factory) this._factory = undefined
    }
  }

  async create(options: AgentCreationOptions): Promise<AgentHandle> {
    if (!this._factory) throw new Error('no agent factory registered')
    const id = options.id ?? options.sessionId ?? randomUUID()
    if (this._agents.has(id)) throw new Error(`agent already registered: ${id}`)
    const handle = await this._factory.create({ ...options, id })
    this._agents.set(id, handle.agent)
    this._owners.set(id, handle.agent.owner)
    this._ctx.emit('agent/created', { id, agent: handle.agent })
    return {
      agent: handle.agent,
      dispose: () => this._dispose(id, handle),
    }
  }

  async resume(options: AgentCreationOptions): Promise<AgentHandle> {
    if (!this._factory) throw new Error('no agent factory registered')
    if (!options.id && !options.sessionId) {
      throw new AgentError('agent resume requires a stable session id')
    }
    const id = options.id ?? options.sessionId!
    if (this._agents.has(id)) throw new Error(`agent already registered: ${id}`)
    const handle = await this._factory.resume({ ...options, id })
    this._agents.set(id, handle.agent)
    this._owners.set(id, handle.agent.owner)
    this._ctx.emit('agent/created', { id, agent: handle.agent })
    return {
      agent: handle.agent,
      dispose: () => this._dispose(id, handle),
    }
  }

  get(id: string): LiveAgent | undefined {
    return this._agents.get(id)
  }

  list(): LiveAgent[] {
    return [...this._agents.values()]
  }

  roots(): LiveAgent[] {
    return this.list().filter(agent => this._owners.get(agent.id) === undefined)
  }

  isOwnedBy(id: string, owner: string): boolean {
    return this._owners.get(id) === owner
  }

  private async _dispose(id: string, handle: AgentHandle): Promise<void> {
    const agent = this._agents.get(id)
    if (!agent) return
    await handle.dispose()
    this._agents.delete(id)
    this._owners.delete(id)
    this._ctx.emit('agent/disposed', { id })
  }
}

async function buildHandle(
  ctx: Context,
  options: AgentCreationOptions,
  resume = false,
): Promise<AgentHandle> {
  const fileMeta = await readAgentMeta(options.file)
  if (resume && !fileMeta) {
    throw new AgentError(`no agent session meta found for resume: ${options.file}`)
  }
  const requestedId = options.id ?? options.sessionId ?? randomUUID()
  if (fileMeta?.agentId && fileMeta.agentId !== requestedId) {
    throw new AgentError(
      `agent id mismatch: session belongs to ${fileMeta.agentId}, requested ${requestedId}`,
    )
  }
  const agentId = fileMeta?.agentId ?? requestedId
  if (!agentId) throw new AgentError('agent requires a stable identity')
  let agentCtx!: Context
  const agentScope = await ctx.inject([], (scopeCtx: Context) => {
    agentCtx = scopeCtx
      .isolate('agentScope')
      .isolate('llm')
      .isolate('tools')
      .isolate('systemPrompt')
    agentCtx.provide('agentScope', agentCtx)
  })
  await agentScope
  const runtimeCtx = createAgentRuntimeContext(agentCtx)
  const log = new SessionLog(options.file, undefined, runtimeCtx)
  try {
    await log.init()
    const fileEvents = await log.read()
    const durableMeta = readDurableAgentMeta(fileEvents)
    if (durableMeta) {
      if (durableMeta.agentId !== agentId) {
        throw new AgentError(
          `agent id mismatch: session belongs to ${durableMeta.agentId}, requested ${agentId}`,
        )
      }
      if (options.agentType && durableMeta.agentType !== options.agentType) {
        throw new AgentError(
          `agent type mismatch: session is ${durableMeta.agentType ?? 'untyped'}, requested ${options.agentType}`,
        )
      }
      if (options.mode && durableMeta.mode !== options.mode) {
        throw new AgentError(
          `agent mode mismatch: session is ${durableMeta.mode ?? 'unset'}, requested ${options.mode}`,
        )
      }
    }
    const boundMeta: AgentSessionMeta = {
      ...fileMeta,
      ...(durableMeta ?? {}),
      ...(options.title ? { title: options.title } : {}),
      ...(options.owner ? { owner: options.owner } : {}),
      ...(options.parentSessionId ? { parentSessionId: options.parentSessionId } : {}),
      ...(options.subagentMode ? { subagentMode: options.subagentMode } : {}),
      ...(options.subagentAudience ? { subagentAudience: options.subagentAudience } : {}),
      ...(options.subagentLabel ? { subagentLabel: options.subagentLabel } : {}),
      ...(options.subagentDepth !== undefined ? { subagentDepth: options.subagentDepth } : {}),
      ...(options.subagentAllowShell !== undefined ? { subagentAllowShell: options.subagentAllowShell } : {}),
      ...(options.subagentAllowNetwork !== undefined ? { subagentAllowNetwork: options.subagentAllowNetwork } : {}),
      ...(options.subagentPermission ? { subagentPermission: options.subagentPermission } : {}),
      ...(options.createdAt !== undefined ? { createdAt: options.createdAt } : {}),
      ...(options.forkedAtMessageId ? { forkedAtMessageId: options.forkedAtMessageId } : {}),
      agentId,
    }
    const resolvedAgentType = options.agentType ?? durableMeta?.agentType
    if (resolvedAgentType) boundMeta.agentType = resolvedAgentType
    const resolvedMode = options.mode ?? durableMeta?.mode
    if (resolvedMode) boundMeta.mode = resolvedMode
    if (!durableMeta) {
      await log.append('meta', {
        kind: 'agent',
        agentId,
        ...(boundMeta.agentType ? { agentType: boundMeta.agentType } : {}),
        ...(boundMeta.mode ? { mode: boundMeta.mode } : {}),
        ...(boundMeta.title ? { title: boundMeta.title } : {}),
        ...(boundMeta.owner ? { owner: boundMeta.owner } : {}),
        ...(boundMeta.parentSessionId ? { parentSessionId: boundMeta.parentSessionId } : {}),
        ...(boundMeta.subagentMode ? { subagentMode: boundMeta.subagentMode } : {}),
        ...(boundMeta.subagentAudience ? { subagentAudience: boundMeta.subagentAudience } : {}),
        ...(boundMeta.subagentLabel ? { subagentLabel: boundMeta.subagentLabel } : {}),
        ...(boundMeta.subagentDepth !== undefined ? { subagentDepth: boundMeta.subagentDepth } : {}),
        ...(boundMeta.subagentAllowShell !== undefined ? { subagentAllowShell: boundMeta.subagentAllowShell } : {}),
        ...(boundMeta.subagentAllowNetwork !== undefined ? { subagentAllowNetwork: boundMeta.subagentAllowNetwork } : {}),
        ...(boundMeta.subagentPermission ? { subagentPermission: boundMeta.subagentPermission } : {}),
        ...(boundMeta.forkedAtMessageId ? { forkedAtMessageId: boundMeta.forkedAtMessageId } : {}),
        ...(boundMeta.createdAt !== undefined ? { createdAt: boundMeta.createdAt } : {}),
      })
    }
    const durable = resume
      ? await DurableInbox.restore(log)
      : new DurableInbox(log)
    const restoredWakeReservation = resume && hasRestoredWakeReservation(fileEvents)
    const injected = new AgentInbox()
    const inboxView: LiveInboxView = {
      get size() {
        return durable.size
      },
      snapshot: () => durable.snapshot(),
    }
    let claimNextStep: () => Promise<readonly AgentInput[]> = async () => []
    const service = new AgentService(runtimeCtx, {
      agentId,
      session: log,
      inbox: injected,
      ...(options.llm ? { llm: options.llm } : {}),
      ...(options.contextWindow !== undefined ? { contextWindow: options.contextWindow } : {}),
      ...(options.maxTurns !== undefined ? { maxTurns: options.maxTurns } : {}),
      ...(options.maxSteps !== undefined ? { maxSteps: options.maxSteps } : {}),
      ...(options.contextBudget ? { contextBudget: options.contextBudget } : {}),
      ...(options.hooks ? { hooks: options.hooks } : {}),
      claimNextStep: () => claimNextStep(),
    })
    if (options.system) injected.inject('agentSystem', options.system)
    const agent = new LiveAgentImpl(
      runtimeCtx,
      agentCtx,
      service,
      agentId,
      inboxView,
      log,
      durable,
      boundMeta,
      options.manualStreaming === true,
      restoredWakeReservation,
    )
    claimNextStep = () => agent.claimNextStepInputs()
    agent.trackSetupDispose(() => agentScope.dispose())
    let setupResult: AgentSetupCommit | void
    try {
      setupResult = await options.setup?.(agentCtx)
      for (const name of ['llm', 'systemPrompt']) {
        if (agentCtx.get(name, false) !== undefined) continue
        const inherited = ctx.get(name, false)
        if (inherited !== undefined) agentCtx.provide(name, inherited)
      }
      const loop = agentCtx.get('agentLoop', false)
      agent.setLoop(loop)
      setupResult?.commit()
    } catch (error) {
      await agent.dispose().catch(() => undefined)
      await log.close().catch(() => undefined)
      throw error
    }
    await agent.publishSessionStart(resume ? 'resume' : 'startup')
    for (const input of options.initial ?? []) {
      if (!resume) await durable.insert({ text: input.text ?? '' })
    }
    // Queued input already continues the work; recovery only fills an empty inbox.
    if (resume && options.autoRecover && !durable.size && findInterruptedTurn(await log.read())?.safe) {
      await recoverInterruptedTurn(agent)
    }
    if (
      resume
      && (durable.snapshot().nextTurn.length || restoredWakeReservation)
      && options.manualStreaming !== true
    ) {
      setTimeout(() => agent.wakeNow(), 0)
    }
    return { agent, dispose: () => agent.dispose() }
  } catch (error) {
    await Promise.resolve().then(() => agentScope.dispose()).catch(() => undefined)
    throw error
  }
}

function hasRestoredWakeReservation(events: readonly SessionEvent[]): boolean {
  type PendingInboxEntry = { wakes: boolean }
  const nextTurn: PendingInboxEntry[] = []
  const nextStep: PendingInboxEntry[] = []
  for (const event of events) {
    if (event.type !== 'agent/inbox/spliced') continue
    const payload = event.payload
    if (payload.target === 'all') {
      nextTurn.splice(0, payload.deleteCounts?.nextTurn ?? nextTurn.length)
      nextStep.splice(0, payload.deleteCounts?.nextStep ?? nextStep.length)
      continue
    }
    const list = payload.target === 'next-step' ? nextStep : nextTurn
    const count = payload.deleteCount ?? 0
    if (count === Number.POSITIVE_INFINITY) {
      list.length = 0
    } else if (count > 0 && payload.index !== undefined) {
      list.splice(payload.index, count)
    }
    let insertedOffset = 0
    for (const inserted of payload.inserted ?? []) {
      const entry = { wakes: payload.target === 'next-step' && inserted.mode === 'steer' }
      if (inserted.mode === 'steer') {
        list.unshift(entry)
      } else {
        const index = payload.index === undefined
          ? list.length
          : Math.min(list.length, payload.index + insertedOffset)
        list.splice(index, 0, entry)
        insertedOffset += 1
      }
    }
  }
  return nextStep.some(entry => entry.wakes)
}

async function readAgentMeta(
  file: string,
): Promise<{ agentId?: string; createdAt?: number } | undefined> {
  const lines = await import('node:fs/promises').then(fs => fs.readFile(file, 'utf8'))
    .catch((error: unknown) => {
      if ((error as NodeJS.ErrnoException).code === 'ENOENT') return undefined
      throw error
    })
  if (lines === undefined) return undefined
  for (const line of lines.split('\n')) {
    if (!line.trim()) continue
    let record: Record<string, unknown>
    try {
      const parsed = JSON.parse(line) as unknown
      record = parsed && typeof parsed === 'object'
        ? parsed as Record<string, unknown>
        : {}
    } catch {
      continue
    }
    if (record.type !== 'meta') continue
    const payload = record.payload as Record<string, unknown> | undefined
    if (!payload || payload.kind !== 'agent') continue
    return {
      ...(typeof payload.agentId === 'string' ? { agentId: payload.agentId } : {}),
      ...(typeof payload.createdAt === 'number' ? { createdAt: payload.createdAt } : {}),
    }
  }
  return undefined
}

function readDurableAgentMeta(events: readonly SessionEvent[]): AgentSessionMeta | undefined {
  for (let index = events.length - 1; index >= 0; index -= 1) {
    const event = events[index]
    if (event?.type !== 'meta') continue
    const payload = event.payload as Record<string, unknown>
    if (payload.kind !== 'agent') continue
    const meta: AgentSessionMeta = {
      agentId: typeof payload.agentId === 'string' ? payload.agentId : '',
    }
    if (isAgentType(payload.agentType)) meta.agentType = payload.agentType
    if (payload.mode === 'execute') meta.mode = 'auto'
    else if (payload.mode === 'auto' || payload.mode === 'plan' || payload.mode === 'goal') {
      meta.mode = payload.mode
    }
    if (typeof payload.title === 'string') meta.title = payload.title
    if (typeof payload.owner === 'string') meta.owner = payload.owner
    if (typeof payload.parentSessionId === 'string') {
      meta.parentSessionId = payload.parentSessionId
    }
    if (typeof payload.forkedAtMessageId === 'string') {
      meta.forkedAtMessageId = payload.forkedAtMessageId
    }
    if (typeof payload.createdAt === 'number') meta.createdAt = payload.createdAt
    if (payload.subagentMode === 'spawn' || payload.subagentMode === 'fork') {
      meta.subagentMode = payload.subagentMode
    }
    if (payload.subagentAudience === 'agent' || payload.subagentAudience === 'user') meta.subagentAudience = payload.subagentAudience
    if (typeof payload.subagentLabel === 'string') meta.subagentLabel = payload.subagentLabel
    if (typeof payload.subagentDepth === 'number') meta.subagentDepth = payload.subagentDepth
    if (typeof payload.subagentAllowShell === 'boolean') meta.subagentAllowShell = payload.subagentAllowShell
    if (typeof payload.subagentAllowNetwork === 'boolean') meta.subagentAllowNetwork = payload.subagentAllowNetwork
    if (payload.subagentPermission === 'read-only' || payload.subagentPermission === 'workspace-write'
      || payload.subagentPermission === 'bypass') meta.subagentPermission = payload.subagentPermission
    return meta
  }
  return undefined
}

export const agents = {
  name: 'agents',
  inject: ['tools'],
  apply: (ctx: Context) => {
    const registry = new AgentRegistry(ctx)
    ctx.provide('agents', registry)
    provideAgentSteer(ctx, {
      has: agentId => registry.get(agentId) !== undefined,
      async steer(agentId, input) {
        await registry.get(agentId)?.steer(input)
      },
      async inject(agentId, input) {
        await registry.get(agentId)?.inject(input)
      },
    })
    registry.setFactory({
      create: (options) => buildHandle(ctx, options),
      resume: (options) => buildHandle(ctx, options, true),
    })
    return async () => {
      // Agent teardown writes durable state (a closing `turn/end`, the cleared inbox).
      // Disposing the Context must wait for it: otherwise the last writes race whatever
      // the host does next (removing the workspace), and nothing surfaces the failure.
      const settled = await Promise.allSettled(registry.list().map(agent => agent.dispose()))
      for (const result of settled) {
        if (result.status !== 'rejected') continue
        try {
          ctx.emit('agent/error', { id: 'registry', error: result.reason })
        } catch {
          // An observer must not turn a contained teardown failure into an unhandled rejection.
        }
      }
    }
  },
} satisfies Plugin

/**
 * Durable inbox payload for an input. Text with images becomes one user
 * message so the attachments survive the inbox round trip.
 */
function inboxContent(input: AgentInput): unknown {
  if (input.messages !== undefined) return input.messages
  if (input.attachments?.length) {
    const message: ModelMessage = { role: 'user', content: input.text ?? '', attachments: input.attachments }
    return [message]
  }
  return input.context
}
